// The harbour plan, a section of the own hold card (round 086 card 07 b,
// R1824, ui-map-interface.md §8 "The card family" and §17.3.60).
//
// A chart of a coast carries a plan of the port with its berths. The hold
// card carries that plan under its round pick: the Harbour's two weight
// buckets, combat and support, as rows of berth squares, with the hulls
// that lie in them. "Is there room for a Basilisk" is then answered by a
// glance at the one card a selected hold opens, and not by a refused order.
//
// **OWN HOLD ONLY.** A stranger's berths are not on the wire.
//
// The section follows the chart reading: the historical reading words the
// lanes ("berths for fighting ships", "8 of 25") and sets them in the Fell
// steps, as the chart cards do; the modern reading keeps the card's chrome
// face. The module is pure: it reads the state and writes markup.

import { Building, type Empire } from '../../gen/proto/aquaphobia';
import { card as S } from '../../gen/strings';
import type { GameState } from '../../state';
import { esc } from '../esc';
import { ratioFig } from '../format';

/** One lane of the Harbour: its words, its two figures and its hulls. */
interface Lane {
  label: string;
  used: number;
  cap: number;
  hulls: string[];
}

/** The two lanes, with the hulls that lie in each. A hull's lane is the
 *  `support` flag of its class in the hello catalog. */
function lanes(s: GameState, empire: Empire, hist: boolean): Lane[] {
  const combat: string[] = [];
  const support: string[] = [];
  for (const hull of empire.stock) {
    const spec = s.ship(hull.cls);
    if (!spec) continue;
    const word = hist ? spec.name : S.plan_hull({ name: spec.name, weight: spec.weight });
    (spec.support ? support : combat).push(word);
  }
  return [
    {
      label: hist ? S.plan_combat_historical : S.plan_combat,
      used: empire.combatWeight, cap: empire.combatCap, hulls: combat,
    },
    {
      label: hist ? S.plan_support_historical : S.plan_support,
      used: empire.supportWeight, cap: empire.supportCap, hulls: support,
    },
  ];
}

/** One lane: the label and the figure, one berth square per point of the
 *  cap, filled up to the weight, and the hulls. */
function laneHtml(l: Lane, hist: boolean): string {
  const cap = Math.max(0, Math.round(l.cap));
  let berths = '';
  for (let k = 0; k < cap; k++) berths += k < l.used ? '<i class="on"></i>' : '<i></i>';
  const fig = hist ? S.plan_of_historical({ used: l.used, cap: l.cap }) : ratioFig(l.used, l.cap);
  return '<div class="ck-lane">'
    + `<div class="ck-lh"><span class="ck-ll">${esc(l.label)}</span><span class="ck-lf">${fig}</span></div>`
    + `<div class="ck-berths">${berths}</div>`
    + (l.hulls.length > 0 ? `<div class="ck-lhulls">${esc(l.hulls.join(' · '))}</div>` : '')
    + '</div>';
}

/**
 * The harbour plan section of the own hold card, or '' before the empire
 * is known. The section head names the plan and, at the right, the
 * Harbour with its level, whose name the catalog holds.
 */
export function harbourPlanHtml(s: GameState, historical: boolean): string {
  const empire = s.empire;
  if (!empire) return '';
  const harbour = s.building(Building.BUILDING_HARBOUR)?.name ?? '';
  const level = s.level(Building.BUILDING_HARBOUR);
  return `<div class="ck-sect ck-plan${historical ? ' hist' : ''}">`
    + '<div class="ck-sh">'
    + `<span class="ck-cap">${esc(historical ? S.plan_head_historical : S.plan_head)}</span>`
    + `<span class="ck-shr">${esc(S.plan_harbour({ building: harbour, level }))}</span></div>`
    + lanes(s, empire, historical).map((l) => laneHtml(l, historical)).join('')
    + harbourLimitsHtml(s, historical)
    + '</div>';
}

/**
 * The one line about harbour limits (v2-the-deep.md §6.3, R2585): the
 * radius of the hold's aura and what it gives, while the flag `modifiers`
 * holds and the catalog states a radius. The figure is the server's
 * (`CatalogRules.harbour_limits_px`); the line states no rate.
 */
function harbourLimitsHtml(s: GameState, historical: boolean): string {
  const px = s.rules.harbourLimitsPx;
  if (!s.hasFeatureFlag('modifiers') || !(px > 0)) return '';
  const line = historical
    ? S.plan_limits_historical({ px: Math.round(px) })
    : S.plan_limits({ px: Math.round(px) });
  return `<div class="ck-lhulls ck-limits">${esc(line)}</div>`;
}
